// frontend/src/components/ShiftVolumeByMonthPanel.jsx
//
// "Shift Volume by Month — by Shift Type" chart, as a standalone panel for
// the admin/manager main dashboards (SchedulingDashboard.jsx,
// ManagerDashboard.jsx) — same chart already on the Agency Dashboards
// (admin + manager Dashboard/AgencyDashboard.jsx), but self-contained with
// its own trailing-months data fetch, since those dashboards don't already
// pull a wide date range of shifts the way the Agency Dashboard's own
// filters do. Bucketing/palette/ward-scoping logic is duplicated from
// AgencyDashboard.jsx / OpenShiftsGridPanel.jsx rather than shared, matching
// how those two already duplicate it between themselves.
import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { useSelector } from 'react-redux';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import { useGetWeeklyShiftsByWardQuery } from '../slices/shiftApiSlice';
import { useGetHospitalLocationsQuery } from '../slices/hospitalLocationApiSlice';

// ─── Palette — same hex values as SchedulingDashboard/ManagerDashboard/
// AgencyDashboard/OpenShiftsGridPanel (not exported from those files, so
// duplicated here). ──────────────────────────────────────────────────────
const NAVY = '#0d1b2a';
const TEAL = '#0a8577';
const TEAL_L = '#0cb89e';
const AMBER = '#e8a020';
const RED = '#c0392b';
const GREEN = '#27ae60';
const SLATE = '#1e2d3d';
const MUTED = '#8fa3b1';
const WHITE = '#f0f4f8';
const BLUE = '#2196f3';
const PURPLE = '#8e44ad';
const CATEGORY_PALETTE = [TEAL_L, BLUE, AMBER, GREEN, RED, PURPLE, '#c0398f', '#5b8def'];

// "Shift type" = the time-of-day pattern (Long Day, Night Duty, ...), same
// preset windows as the Ward Weekly View / staff HomeScreen grid /
// OpenShiftsGridPanel / AgencyDashboard, so it means the same thing
// everywhere in the app — not the staff category (RGN, HCA, ...), which is
// its own filter below.
const SHIFT_TYPE_BUCKETS = [
  { key: 'LD',  label: 'Long Day',          start: '08:00', end: '20:00' },
  { key: 'ND',  label: 'Night Duty',        start: '20:00', end: '08:00' },
  { key: 'AM',  label: 'Morning',           start: '08:00', end: '16:00' },
  { key: 'PM',  label: 'Afternoon',         start: '14:00', end: '20:00' },
  { key: 'TW',  label: 'Twilight',          start: '20:00', end: '00:00' },
  { key: 'ELD', label: 'Extended Long Day', start: '07:30', end: '20:30' },
];
const ESCORT_ROW = { key: 'ESCORT', label: 'Escort' };
const SHIFT_TYPE_ROWS = [...SHIFT_TYPE_BUCKETS, ESCORT_ROW].map((row, i) => ({
  ...row, color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
}));

const timeToMinutes = (t) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};
const bucketDistance = (shiftStartMin, shiftEndMin, bucket) => {
  const bStart = timeToMinutes(bucket.start);
  let bEnd = timeToMinutes(bucket.end);
  if (bEnd <= bStart) bEnd += 1440;
  let sEnd = shiftEndMin;
  if (sEnd <= shiftStartMin) sEnd += 1440;
  return Math.abs(shiftStartMin - bStart) + Math.abs(sEnd - bEnd);
};
// Best-fit bucket by start/end time — same matching logic as
// OpenShiftsGridPanel.jsx's pickBucketKey / the AgencyDashboards'
// pickShiftTypeKey, so a shift lands in the same "shift type" everywhere.
const pickShiftTypeKey = (shift) => {
  if (shift.escort) return ESCORT_ROW.key;
  if (!shift.startDateTime || !shift.endDateTime) return SHIFT_TYPE_BUCKETS[0].key;
  const start = new Date(shift.startDateTime);
  const end = new Date(shift.endDateTime);
  const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
  const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
  const sMin = timeToMinutes(startTime);
  const eMin = timeToMinutes(endTime);
  let best = SHIFT_TYPE_BUCKETS[0];
  let bestDist = Infinity;
  SHIFT_TYPE_BUCKETS.forEach((bucket) => {
    const dist = bucketDistance(sMin, eMin, bucket);
    if (dist < bestDist) { bestDist = dist; best = bucket; }
  });
  return best.key;
};

const toLocalISO = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const monthKey = (dateStr) => {
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

// ── Drilldown modal helpers ─────────────────────────────────────────────
const fmtDrilldownDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
const shiftMinsFor = (shift) => {
  if (!shift.startDateTime || !shift.endDateTime) return 0;
  return Math.round((new Date(shift.endDateTime) - new Date(shift.startDateTime)) / 60000);
};
const fmtHours = (mins) => {
  if (!mins) return '0h';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};
const shiftStatusLabelFor = (shift) => {
  if (shift.lineStatus === 'FilledByStaff') return 'Filled — Staff';
  if (shift.lineStatus === 'FilledByAgency') return 'Filled — Agency';
  return shift.lineStatus || 'Unknown';
};

// Same "agency shift" definition getAgencyShifts() uses on the backend:
// filledByAgency or actualFilledByAgency set. allowAgencyView alone only
// ever applies to a still-Open, unfilled shift.
const isAgencyShift = (shift) =>
  shift.allowAgencyView === true || !!shift.filledByAgency || !!shift.actualFilledByAgency;

// Manager ward-scoping — identical logic to OpenShiftsGridPanel.jsx /
// ManagerDashboard.jsx's `directorateWards`, so a manager sees the same
// wards here as everywhere else on their dashboard. Admins (and anyone with
// no manager role at all, e.g. a reporter) get every ward, unscoped.
const normalizeDirectorate = (val) => (val || '').trim().toLowerCase();

const useDirectorateWards = () => {
  const { userInfo } = useSelector((s) => s.auth);
  const isAdmin = userInfo?.isAdmin === true;
  const isManager = userInfo?.isManager === true;
  const managerDirectorate = userInfo?.directorateName || null;
  const managerOnlyOwnWard = userInfo?.onlyOwnWard === true;

  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery(undefined, {
    skip: isAdmin || !isManager,
  });

  const inclusionIds = useMemo(() => {
    const managerWardInclusions = userInfo?.wardAreaInclusions || [];
    return new Set(managerWardInclusions.map((w) => (typeof w === 'object' && w !== null ? w._id : w)));
  }, [userInfo]);

  const normalizedManagerDirectorate = useMemo(
    () => normalizeDirectorate(managerDirectorate),
    [managerDirectorate]
  );

  const directorateWards = useMemo(() => {
    if (isAdmin || !isManager) return null;

    if (managerOnlyOwnWard) {
      return new Set(
        hospitalLocations
          .filter((l) => l.wardDepartmentArea === userInfo?.speciality || inclusionIds.has(l._id))
          .map((l) => l.wardDepartmentArea)
          .filter(Boolean)
      );
    }

    if (!managerDirectorate && inclusionIds.size === 0) return new Set();

    return new Set(
      hospitalLocations
        .filter(
          (l) =>
            normalizeDirectorate(l.directorate) === normalizedManagerDirectorate || inclusionIds.has(l._id)
        )
        .map((l) => l.wardDepartmentArea)
        .filter(Boolean)
    );
  }, [hospitalLocations, isAdmin, isManager, managerOnlyOwnWard, managerDirectorate, normalizedManagerDirectorate, inclusionIds, userInfo?.speciality]);

  return { directorateWards, isManagerScoped: isManager && !isAdmin, managerDirectorate, managerOnlyOwnWard };
};

// How many trailing months (including the current one) to pull and show.
const MONTHS_BACK = 6;

const inlineSelectSx = {
  minWidth: 150,
  '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE, borderRadius: 1.5,
    '& fieldset': { borderColor: `${TEAL}44` },
    '&:hover fieldset': { borderColor: TEAL },
    '&.Mui-focused fieldset': { borderColor: TEAL },
  },
  '& .MuiSvgIcon-root': { color: MUTED },
};
const inlineMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: SLATE, border: `1px solid ${TEAL}44`,
      '& .MuiMenuItem-root': {
        color: WHITE, fontFamily: '"DM Mono", monospace', fontSize: '0.8rem',
        '&:hover': { bgcolor: `${TEAL}44`, color: WHITE },
        '&.Mui-selected': { bgcolor: `${TEAL}33`, color: TEAL_L, '&:hover': { bgcolor: `${TEAL}55` } },
      },
    },
  },
};

// `startDate`/`endDate` (YYYY-MM-DD strings): when the parent dashboard
// already has its own date-range filter form (SchedulingDashboard.jsx /
// ManagerDashboard.jsx both do — `dateFrom`/`dateTo`, same as
// AgencyDashboard.jsx), pass that range in here so this panel re-uses the
// same shift fetch/date window instead of firing its own separate query —
// matches how AgencyDashboard.jsx already drives its identical chart off
// the page's own filter-selected dates. If omitted (panel used standalone),
// falls back to its own trailing-MONTHS_BACK window.
const ShiftVolumeByMonthPanel = ({ startDate: startDateProp, endDate: endDateProp } = {}) => {
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [selectedStaffType, setSelectedStaffType] = useState('');
  const [agencyOnly, setAgencyOnly] = useState(false);
  // The shift list behind a clicked bar — { title, rows } — or null when the
  // drilldown modal is closed.
  const [drilldown, setDrilldown] = useState(null);

  const { directorateWards, isManagerScoped, managerDirectorate, managerOnlyOwnWard } = useDirectorateWards();

  const defaultRange = useMemo(() => {
    const now = new Date();
    const rangeStart = new Date(now.getFullYear(), now.getMonth() - (MONTHS_BACK - 1), 1);
    const rangeEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate: toLocalISO(rangeStart), endDate: toLocalISO(rangeEnd) };
  }, []);

  const startDate = startDateProp || defaultRange.startDate;
  const endDate = endDateProp || defaultRange.endDate;

  // No `ward` passed — every ward, every non-cancelled shift in range (ward
  // scoping for managers is applied client-side below, same as
  // OpenShiftsGridPanel), same source the Ward Weekly View itself uses.
  const { data: shifts, isLoading } = useGetWeeklyShiftsByWardQuery({ startDate, endDate });

  const scopedShifts = useMemo(
    () => (shifts || []).filter((s) => !directorateWards || directorateWards.has(s.wardDepartmentArea)),
    [shifts, directorateWards]
  );

  const staffTypeOptions = useMemo(() => {
    const set = new Set();
    scopedShifts.forEach((s) => set.add(s.categoryOfStaff || 'Unspecified'));
    return Array.from(set).sort();
  }, [scopedShifts]);

  // Same filtered set the chart's counts are built from — kept as its own
  // memo so a bar click can re-derive the exact shifts behind a (month,
  // shift type) cell without duplicating the filter logic.
  const chartSource = useMemo(
    () => scopedShifts
      .filter((s) => !agencyOnly || isAgencyShift(s))
      .filter((s) => !selectedStaffType || (s.categoryOfStaff || 'Unspecified') === selectedStaffType),
    [scopedShifts, agencyOnly, selectedStaffType]
  );

  const byMonthByShiftType = useMemo(() => {
    const source = chartSource;

    const map = {};
    source.forEach((s) => {
      if (!s.startDateTime) return;
      const key = monthKey(s.startDateTime);
      if (!map[key]) {
        map[key] = { total: 0, byType: {} };
        SHIFT_TYPE_ROWS.forEach((row) => { map[key].byType[row.key] = 0; });
      }
      map[key].total++;
      const typeKey = pickShiftTypeKey(s);
      map[key].byType[typeKey] = (map[key].byType[typeKey] || 0) + 1;
    });

    return Object.entries(map)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, d]) => ({
        key,
        label: monthLabel(key),
        total: d.total,
        categories: SHIFT_TYPE_ROWS.map((row) => ({
          key: row.key, label: row.label, color: row.color, count: d.byType[row.key] || 0,
        })),
      }));
  }, [chartSource]);

  // Agency share for the month shown, independent of the "Agency shifts
  // only" switch itself (so the switch's own % readout doesn't collapse to
  // 100% the moment it's turned on) — still respects the staff-type filter,
  // so the % matches whatever the chart is currently scoped to.
  const monthAgencyStats = useMemo(() => {
    const source = scopedShifts.filter(
      (s) => !selectedStaffType || (s.categoryOfStaff || 'Unspecified') === selectedStaffType
    );
    const map = {};
    source.forEach((s) => {
      if (!s.startDateTime) return;
      const key = monthKey(s.startDateTime);
      if (!map[key]) map[key] = { total: 0, agency: 0 };
      map[key].total++;
      if (isAgencyShift(s)) map[key].agency++;
    });
    return map;
  }, [scopedShifts, selectedStaffType]);

  const effectiveMonthKey = byMonthByShiftType.some((m) => m.key === selectedMonthKey)
    ? selectedMonthKey
    : (byMonthByShiftType[byMonthByShiftType.length - 1]?.key || null);

  const monthKeys = byMonthByShiftType.map((m) => m.key);
  const idx = monthKeys.indexOf(effectiveMonthKey);
  const current = idx >= 0 ? byMonthByShiftType[idx] : null;
  const maxCount = current ? Math.max(1, ...current.categories.map((c) => c.count)) : 1;
  const trackHeight = 150;

  // Opens the drilldown modal with the exact shifts behind one (month,
  // shift type) bar — re-filters chartSource rather than storing rows on
  // every category up front, since only one bar's list is ever needed.
  const handleBarClick = (row) => {
    if (!current) return;
    const rows = chartSource.filter(
      (s) =>
        s.startDateTime &&
        monthKey(s.startDateTime) === current.key &&
        pickShiftTypeKey(s) === row.key
    );
    setDrilldown({
      title: `${row.label} — ${current.label}${selectedStaffType ? ` · ${selectedStaffType}` : ''}`,
      rows,
    });
  };

  const currentAgencyStats = effectiveMonthKey ? monthAgencyStats[effectiveMonthKey] : null;
  const currentAgencyPct = currentAgencyStats && currentAgencyStats.total > 0
    ? Math.round((currentAgencyStats.agency / currentAgencyStats.total) * 100)
    : 0;

  const goPrev = () => idx > 0 && setSelectedMonthKey(monthKeys[idx - 1]);
  const goNext = () => idx >= 0 && idx < monthKeys.length - 1 && setSelectedMonthKey(monthKeys[idx + 1]);

  const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: TEAL_L },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: TEAL },
  };
  const labelSx = { color: MUTED, fontSize: '0.72rem', fontFamily: '"DM Mono",monospace' };

  return (
    <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <AssessmentIcon sx={{ color: TEAL, fontSize: 20 }} />
          <Typography sx={{ fontFamily: '"DM Mono",monospace', fontWeight: 700, fontSize: '0.85rem',
            letterSpacing: 1, textTransform: 'uppercase', color: WHITE }}>
            Shift Volume by Month — by Shift Type
          </Typography>
          {isManagerScoped && (
            <Typography
              title="This chart only counts wards in your directorate (plus any admin-assigned inclusions) — same scope as the rest of this dashboard"
              sx={{ color: MUTED, fontSize: '0.68rem', fontFamily: '"DM Mono",monospace' }}
            >
              · {managerOnlyOwnWard ? 'my ward' : managerDirectorate || 'no directorate assigned'}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {currentAgencyStats && (
            <Tooltip title={`${currentAgencyStats.agency} of ${currentAgencyStats.total} shifts in ${current?.label || 'this month'} are agency${selectedStaffType ? ` (${selectedStaffType})` : ''}`} arrow>
              <Typography sx={{ ...labelSx, color: AMBER, fontWeight: 700 }}>
                {currentAgencyPct}% agency
              </Typography>
            </Tooltip>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={switchSx}
                checked={agencyOnly}
                onChange={(e) => setAgencyOnly(e.target.checked)}
              />
            }
            label={<Typography sx={labelSx}>Agency shifts only</Typography>}
            title="Show only shifts an agency is filling or has filled, or that are flagged visible to agencies"
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} sx={{ color: TEAL }} />
        </Box>
      ) : byMonthByShiftType.length === 0 ? (
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace' }}>No data</Typography>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <IconButton size="small" onClick={goPrev} disabled={idx <= 0}
              sx={{ color: idx <= 0 ? `${MUTED}66` : TEAL_L }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <FormControl size="small" sx={inlineSelectSx}>
              <Select
                value={current?.key || ''}
                onChange={(e) => setSelectedMonthKey(e.target.value)}
                sx={{ fontSize: '0.8rem', fontFamily: '"DM Mono", monospace', textAlign: 'center' }}
                MenuProps={inlineMenuProps}
              >
                {byMonthByShiftType.map((m) => (
                  <MenuItem key={m.key} value={m.key}>{m.label} ({m.total})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton size="small" onClick={goNext} disabled={idx < 0 || idx >= monthKeys.length - 1}
              sx={{ color: (idx < 0 || idx >= monthKeys.length - 1) ? `${MUTED}66` : TEAL_L }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>

            {/* Staff type (categoryOfStaff) — a separate filter from shift
                type, sitting beside the month selector, defaulting to "All". */}
            <FormControl size="small" sx={{ ...inlineSelectSx, ml: 1 }}>
              <Select
                value={selectedStaffType}
                onChange={(e) => setSelectedStaffType(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.8rem', fontFamily: '"DM Mono", monospace', textAlign: 'center' }}
                MenuProps={inlineMenuProps}
              >
                <MenuItem value=""><em style={{ fontStyle: 'normal' }}>All Staff Types</em></MenuItem>
                {staffTypeOptions.map((o) => (
                  <MenuItem key={o} value={o}>{o}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {!current || current.total === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: '"DM Mono",monospace', textAlign: 'center' }}>
              No shifts in {current?.label || 'this month'}{selectedStaffType ? ` for ${selectedStaffType}` : ''}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 1, sm: 2.5 }, height: trackHeight + 55, px: 1, justifyContent: 'center', overflowX: 'auto' }}>
              {current.categories.map((row) => {
                const px = (row.count / maxCount) * trackHeight;
                return (
                  <Box
                    key={row.key}
                    onClick={() => handleBarClick(row)}
                    sx={{
                      flex: '0 0 auto', minWidth: 56, display: 'flex', flexDirection: 'column', alignItems: 'center',
                      cursor: 'pointer', borderRadius: 1, py: 0.5,
                      '&:hover': { bgcolor: `${TEAL}14` },
                    }}
                  >
                    <Typography sx={{ fontSize: '0.65rem', color: WHITE, fontFamily: '"DM Mono",monospace', mb: 0.5, fontWeight: 700, textAlign: 'center' }}>
                      {row.count}
                      <Typography component="span" sx={{ fontSize: '0.58rem', color: MUTED, fontWeight: 400, ml: 0.4 }}>
                        ({current.total > 0 ? Math.round((row.count / current.total) * 100) : 0}%)
                      </Typography>
                    </Typography>
                    <Box sx={{ height: trackHeight, width: '100%', maxWidth: 44, display: 'flex', alignItems: 'flex-end' }}>
                      <Tooltip title={`${row.count} ${row.label} shift${row.count === 1 ? '' : 's'} in ${current.label}`} arrow>
                        <Box sx={{ width: '100%', height: `${px}px`, borderRadius: '3px 3px 0 0',
                          bgcolor: row.count === 0 ? `${row.color}33` : row.color, transition: 'height 0.3s ease' }} />
                      </Tooltip>
                    </Box>
                    <Typography sx={{ fontSize: '0.62rem', color: MUTED, fontFamily: '"DM Mono",monospace', mt: 0.75,
                      whiteSpace: 'nowrap', maxWidth: 74, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Drilldown modal — the shifts behind whichever bar was clicked. */}
      <Dialog open={!!drilldown} onClose={() => setDrilldown(null)} maxWidth="md" fullWidth>
        {drilldown && (
          <>
            <DialogTitle sx={{ bgcolor: NAVY, color: WHITE, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontFamily: '"DM Mono",monospace', fontSize: '0.95rem', fontWeight: 700 }}>
                {drilldown.title} · {drilldown.rows.length} shift{drilldown.rows.length === 1 ? '' : 's'}
              </Typography>
              <IconButton onClick={() => setDrilldown(null)} sx={{ color: MUTED }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: SLATE, p: 0 }}>
              {drilldown.rows.length === 0 ? (
                <Typography sx={{ color: MUTED, fontSize: '0.8rem', fontFamily: '"DM Mono",monospace', p: 3, textAlign: 'center' }}>
                  No shifts to show.
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Date', 'Ward', 'Status', 'Filled By', 'Job Title', 'Hours'].map((h) => (
                          <TableCell key={h} sx={{ bgcolor: NAVY, color: MUTED, fontFamily: '"DM Mono",monospace', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${TEAL}33` }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drilldown.rows.map((s) => (
                        <TableRow key={s._id} hover>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {fmtDrilldownDate(s.startDateTime)}
                          </TableCell>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {s.wardDepartmentArea || ''}
                          </TableCell>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {shiftStatusLabelFor(s)}
                          </TableCell>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {s.filledByName || s.filledByAgencyName || s.actualFilledByAgencyName || ''}
                          </TableCell>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {s.jobtitle || ''}
                          </TableCell>
                          <TableCell sx={{ color: WHITE, fontFamily: '"DM Mono",monospace', fontSize: '0.72rem', borderBottom: `1px solid ${TEAL}1a` }}>
                            {fmtHours(shiftMinsFor(s))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default ShiftVolumeByMonthPanel;
